import {ChronoUnit} from '@js-joda/core';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import InsertIcon from '@material-ui/icons/UnfoldLess';
import React, {useCallback, useMemo, useState} from 'react';
import {TimeEntry} from '../../pages/TimeEntries';
import OverlapRow from './OverlapRow';
import TimeEntryFormDialog from './TimeEntryFormDialog';

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    },
}));

type Props = {
    onInsert : (timeEntry : TimeEntry) => Promise<void>;
    previousTimeEntry : TimeEntry;
    nextTimeEntry : TimeEntry;
};

const GapRow : React.FC<Props> = ({onInsert, previousTimeEntry, nextTimeEntry} : Props) => {
    const classes = useStyles();
    const [formDialogOpen, setFormDialogOpen] = useState(false);

    const [previousEndDateTime, nextStartDateTime] = useMemo(() => {
        return [
            previousTimeEntry.startTime && previousTimeEntry.minutes
                ? previousTimeEntry.startDate
                    .atTime(previousTimeEntry.startTime)
                    .plusMinutes(previousTimeEntry.minutes)
                : null,
            nextTimeEntry.startTime ? nextTimeEntry.startDate.atTime(nextTimeEntry.startTime) : null,
        ];
    }, [previousTimeEntry, nextTimeEntry]);

    const initialValues : Partial<TimeEntry> = useMemo(() => {
        if (!previousEndDateTime || !nextStartDateTime) {
            return {};
        }

        return {
            startDate: previousEndDateTime.toLocalDate(),
            startTime: previousEndDateTime.toLocalTime(),
            minutes: previousEndDateTime.until(nextStartDateTime, ChronoUnit.MINUTES),
        };
    }, [previousEndDateTime, nextStartDateTime]);

    const handleSubmit = useCallback(async (timeEntry) => {
        setFormDialogOpen(false);
        return onInsert(timeEntry);
    }, [setFormDialogOpen, onInsert]);

    if (!previousEndDateTime || !nextStartDateTime || previousEndDateTime.equals(nextStartDateTime)) {
        return null;
    }

    if (previousEndDateTime > nextStartDateTime) {
        return <OverlapRow/>;
    }

    return (
        <TableRow className={classes.root}>
            <TableCell/>
            <Hidden smDown>
                <TableCell/>
                <TableCell/>
                <TableCell/>
            </Hidden>
            <TableCell colSpan={2}/>
            <TableCell>
                <Tooltip title="Insert between">
                    <IconButton onClick={() => setFormDialogOpen(true)} size="small"><InsertIcon/></IconButton>
                </Tooltip>

                <TimeEntryFormDialog
                    timeEntry={initialValues}
                    open={formDialogOpen}
                    onClose={() => setFormDialogOpen(false)}
                    onSubmit={handleSubmit}
                />
            </TableCell>
        </TableRow>
    );
};

export default GapRow;
